import type { CameraSlice } from '../../core/camera';
import {
  type Feature,
  type FeatureConfig,
  insideCanvas,
} from '../../core/feature-system';
import { normalizeWheelDelta } from '../../core/utils';
import { panSlice, type PanSlice } from './slice';

export type PanConfig = FeatureConfig<{
  /**
   * Pan speed multiplier applied to wheel deltas.
   * A value of 1.0 means 1:1 mapping between wheel pixels and canvas
   * translation.
   * @default 1.0
   */
  speed?: number;
  /**
   * When `true`, panning is clamped to canvas bounds so the user cannot scroll
   * past the document edges. When `false`, panning is unconstrained.
   * @default true
   */
  bounded?: boolean;
}>;

const DEFAULT_CONFIG: Required<Omit<PanConfig, 'shouldRun'>> = {
  speed: 1.0,
  bounded: true,
};

type PanFeature = Feature<CameraSlice, PanSlice, PanConfig>;

export const createPanFeature = (config?: PanConfig): PanFeature => ({
  id: 'pan',
  slice: panSlice,
  config,

  createHandler: (handlerConfig?: PanConfig) => {
    const cfg = { ...DEFAULT_CONFIG, ...config, ...handlerConfig };

    return {
      id: 'pan',
      shouldRun: (event, ctx) => {
        if (
          event.type !== 'wheel' ||
          event.rawEvent.metaKey ||
          !insideCanvas(event)
        ) {
          return false;
        }
        return cfg.shouldRun ? cfg.shouldRun(event, ctx) : true;
      },
      handleEvent: (event, ctx) => {
        if (event.type !== 'wheel') {
          return undefined;
        }

        const state = ctx.store.getState();
        const { deltaX, deltaY } = normalizeWheelDelta(event.rawEvent);

        const speed = state.pan.speed ?? cfg.speed;

        state.pan.actions.panBy({
          x: deltaX * speed,
          y: deltaY * speed,
        });

        return { stopPropagation: true, startLock: true };
      },

      onMount: ctx => {
        const state = ctx.store.getState();
        state.pan.actions.setConfig({
          speed: cfg.speed,
          bounded: cfg.bounded,
        });
      },
    };
  },
});
